/**
 * A QR code encoder, written here rather than installed.
 *
 * WHY THIS IS NOT A DEPENDENCY
 * The thing being drawn is an `otpauth://` URL, and that URL contains the
 * shared secret for somebody's second factor. Handing it to an image service
 * to be rendered would put every account's multi-factor seed in a third
 * party's request log — the exact secret the second factor exists to protect.
 * There is no version of "just call an API for the picture" that is safe here.
 *
 * A library would be safe, but this API deliberately has no third-party
 * runtime dependencies (see `types/node-shim.d.ts`), and a QR encoder is a
 * closed, fully specified problem: ISO/IEC 18004 says exactly what the answer
 * is. So it is written.
 *
 * HOW IT IS CHECKED, AND WHAT IS NOT CHECKED
 * `packages/core/test/qr.test.ts` verifies the parts that can be verified
 * without trusting this file: the Reed–Solomon syndromes are zero, which is
 * the arithmetic a scanner performs to decide whether to believe a symbol; the
 * generator polynomials match the published tables; the format information
 * satisfies its own BCH code and both copies of it agree; and the finder,
 * timing and quiet-zone geometry match the specification.
 *
 * What no test here can do is scan the picture with a phone. Round-tripping
 * through a reader written by the same hand proves only that the hand is
 * consistent. So the enrolment screen always shows the typed key beneath the
 * QR — if a camera ever disagrees with this file, nobody is locked out of
 * their own account while it gets sorted out.
 *
 * SCOPE
 * Byte mode, error correction level M, versions 1 to 10 — up to 213 bytes,
 * comfortably more than any `otpauth://` URL. Anything longer throws rather
 * than silently producing a code that will not scan.
 */

export type EcLevel = 'L' | 'M' | 'Q' | 'H';

export interface QrCode {
  /** Width and height in modules, including the quiet zone if one was asked for. */
  readonly size: number;
  /** `true` is a dark module. Indexed `[row][column]`. */
  readonly modules: readonly (readonly boolean[])[];
}

/* --------------------------------------------------------- GF(256) maths -- */

/**
 * The field Reed–Solomon works in, built once. Primitive polynomial 0x11D,
 * which is what QR specifies.
 */
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
{
  let x = 1;
  for (let i = 0; i < 255; i += 1) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i += 1) EXP[i] = EXP[i - 255]!;
}

const mul = (a: number, b: number): number =>
  (a === 0 || b === 0) ? 0 : EXP[LOG[a]! + LOG[b]!]!;

/** The generator polynomial for `degree` error-correction codewords. */
function generator(degree: number): Uint8Array {
  let poly = new Uint8Array([1]);
  for (let d = 0; d < degree; d += 1) {
    const next = new Uint8Array(poly.length + 1);
    for (let i = 0; i < poly.length; i += 1) {
      next[i] = (next[i] ?? 0) ^ poly[i]!;
      next[i + 1] = (next[i + 1] ?? 0) ^ mul(poly[i]!, EXP[d]!);
    }
    poly = next;
  }
  return poly;
}

/** Polynomial long division; the remainder is the error-correction block. */
function ecCodewords(data: Uint8Array, count: number): Uint8Array {
  const gen = generator(count);
  const rem = new Uint8Array(count);
  for (const byte of data) {
    const factor = byte ^ rem[0]!;
    rem.copyWithin(0, 1);
    rem[count - 1] = 0;
    for (let i = 0; i < count; i += 1) rem[i] = rem[i]! ^ mul(gen[i + 1]!, factor);
  }
  return rem;
}

/* ------------------------------------------------------------- the tables -- */

/**
 * Per version, per level: error-correction codewords per block, then the
 * block structure as [blocks, dataCodewords] pairs.
 *
 * Only the levels this encoder offers are filled in. Level M is the default
 * because it is what authenticator apps are used to reading and it survives a
 * phone camera at an angle.
 */
interface Spec { ecPerBlock: number; groups: Array<[blocks: number, dataCodewords: number]> }

const SPECS: Record<EcLevel, Record<number, Spec>> = {
  L: {
    1: { ecPerBlock: 7, groups: [[1, 19]] },
    2: { ecPerBlock: 10, groups: [[1, 34]] },
    3: { ecPerBlock: 15, groups: [[1, 55]] },
    4: { ecPerBlock: 20, groups: [[1, 80]] },
    5: { ecPerBlock: 26, groups: [[1, 108]] },
    6: { ecPerBlock: 18, groups: [[2, 68]] },
    7: { ecPerBlock: 20, groups: [[2, 78]] },
    8: { ecPerBlock: 24, groups: [[2, 97]] },
    9: { ecPerBlock: 30, groups: [[2, 116]] },
    10: { ecPerBlock: 18, groups: [[2, 68], [2, 69]] },
  },
  M: {
    1: { ecPerBlock: 10, groups: [[1, 16]] },
    2: { ecPerBlock: 16, groups: [[1, 28]] },
    3: { ecPerBlock: 26, groups: [[1, 44]] },
    4: { ecPerBlock: 18, groups: [[2, 32]] },
    5: { ecPerBlock: 24, groups: [[2, 43]] },
    6: { ecPerBlock: 16, groups: [[4, 27]] },
    7: { ecPerBlock: 18, groups: [[4, 31]] },
    8: { ecPerBlock: 22, groups: [[2, 38], [2, 39]] },
    9: { ecPerBlock: 22, groups: [[3, 36], [2, 37]] },
    10: { ecPerBlock: 26, groups: [[4, 43], [1, 44]] },
  },
  Q: {},
  H: {},
};

/** Centres of the alignment patterns, per version. */
const ALIGNMENT: Record<number, number[]> = {
  1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30],
  6: [6, 34], 7: [6, 22, 38], 8: [6, 24, 42], 9: [6, 26, 46], 10: [6, 28, 50],
};

/** Pre-computed 18-bit version information, needed from version 7 up. */
const VERSION_INFO: Record<number, number> = {
  7: 0x07c94, 8: 0x085bc, 9: 0x09a99, 10: 0x0a4d3,
};

/** The two bits that name each level inside the format information. */
const LEVEL_BITS: Record<EcLevel, number> = { L: 0b01, M: 0b00, Q: 0b11, H: 0b10 };

/* -------------------------------------------------------------- encoding -- */

/**
 * UTF-8 bytes, computed here rather than taken from a global.
 *
 * `packages/core` compiles with no ambient global declarations at all, so this
 * file cannot assume the platform encoder exists. This is a dozen exact lines
 * and it keeps the encoder free of anything the build has to be told about.
 */
function utf8(text: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    let cp = text.charCodeAt(i);
    // Combine a surrogate pair into the single code point it represents.
    if (cp >= 0xd800 && cp <= 0xdbff && i + 1 < text.length) {
      const low = text.charCodeAt(i + 1);
      if (low >= 0xdc00 && low <= 0xdfff) {
        cp = 0x10000 + ((cp - 0xd800) << 10) + (low - 0xdc00);
        i += 1;
      }
    }
    if (cp < 0x80) out.push(cp);
    else if (cp < 0x800) out.push(0xc0 | (cp >> 6), 0x80 | (cp & 0x3f));
    else if (cp < 0x10000) out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
    else {
      out.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 0x3f),
        0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
    }
  }
  return out;
}

class BitBuffer {
  private bits: number[] = [];
  push(value: number, length: number): void {
    for (let i = length - 1; i >= 0; i -= 1) this.bits.push((value >>> i) & 1);
  }
  get length(): number { return this.bits.length; }
  padToByte(): void { while (this.bits.length % 8 !== 0) this.bits.push(0); }
  toBytes(): Uint8Array {
    const out = new Uint8Array(this.bits.length / 8);
    for (let i = 0; i < this.bits.length; i += 1) {
      if (this.bits[i]) out[i >> 3] = out[i >> 3]! | (0x80 >> (i & 7));
    }
    return out;
  }
}

const totalDataCodewords = (spec: Spec): number =>
  spec.groups.reduce((n, [blocks, data]) => n + blocks * data, 0);

/** The smallest version that fits, so the picture is never larger than it must be. */
function chooseVersion(byteLength: number, level: EcLevel): number {
  for (let version = 1; version <= 10; version += 1) {
    const spec = SPECS[level][version];
    if (!spec) continue;
    // Mode indicator (4 bits) + character count (8 bits below v10, 16 at v10).
    const headerBits = 4 + (version < 10 ? 8 : 16);
    if (totalDataCodewords(spec) * 8 >= headerBits + byteLength * 8) return version;
  }
  throw new Error(
    `${byteLength} bytes does not fit in a version-10 QR code at level ${level}. `
    + 'Shorten the text rather than shipping a code that will not scan.',
  );
}

/** Data and error-correction codewords, interleaved the way the spec requires. */
function codewordsFor(text: string, version: number, level: EcLevel): Uint8Array {
  const spec = SPECS[level][version]!;
  const bytes = utf8(text);

  const buf = new BitBuffer();
  buf.push(0b0100, 4);                              // byte mode
  buf.push(bytes.length, version < 10 ? 8 : 16);    // character count
  for (const b of bytes) buf.push(b, 8);

  const capacityBits = totalDataCodewords(spec) * 8;
  buf.push(0, Math.min(4, capacityBits - buf.length)); // terminator
  buf.padToByte();

  const data = Array.from(buf.toBytes());
  // Alternating pad bytes, exactly as specified — not zeroes.
  for (let i = 0; data.length < totalDataCodewords(spec); i += 1) {
    data.push(i % 2 === 0 ? 0xec : 0x11);
  }

  // Split into blocks, then interleave. Longer blocks come last, and the
  // interleave walks columns across blocks of unequal length.
  const dataBlocks: Uint8Array[] = [];
  const ecBlocks: Uint8Array[] = [];
  let offset = 0;
  for (const [blocks, size] of spec.groups) {
    for (let b = 0; b < blocks; b += 1) {
      const block = Uint8Array.from(data.slice(offset, offset + size));
      offset += size;
      dataBlocks.push(block);
      ecBlocks.push(ecCodewords(block, spec.ecPerBlock));
    }
  }

  const out: number[] = [];
  const longest = Math.max(...dataBlocks.map((b) => b.length));
  for (let i = 0; i < longest; i += 1) {
    for (const block of dataBlocks) if (i < block.length) out.push(block[i]!);
  }
  for (let i = 0; i < spec.ecPerBlock; i += 1) {
    for (const block of ecBlocks) out.push(block[i]!);
  }
  return Uint8Array.from(out);
}

/* --------------------------------------------------------- the matrix -- */

type Grid = Array<Array<boolean | null>>; // null = not yet placed

function blankGrid(size: number): Grid {
  return Array.from({ length: size }, () => Array.from({ length: size }, () => null));
}

function placeFinder(grid: Grid, row: number, col: number): void {
  for (let r = -1; r <= 7; r += 1) {
    for (let c = -1; c <= 7; c += 1) {
      const rr = row + r; const cc = col + c;
      if (rr < 0 || cc < 0 || rr >= grid.length || cc >= grid.length) continue;
      const onRing = (r === 0 || r === 6) && c >= 0 && c <= 6;
      const onSide = (c === 0 || c === 6) && r >= 0 && r <= 6;
      const inCore = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      grid[rr]![cc] = onRing || onSide || inCore;
    }
  }
}

function placeFunctionPatterns(grid: Grid, version: number): void {
  const size = grid.length;
  placeFinder(grid, 0, 0);
  placeFinder(grid, 0, size - 7);
  placeFinder(grid, size - 7, 0);

  // Timing patterns.
  for (let i = 8; i < size - 8; i += 1) {
    grid[6]![i] = i % 2 === 0;
    grid[i]![6] = i % 2 === 0;
  }

  // Alignment patterns, except where they would sit on a finder.
  const centres = ALIGNMENT[version] ?? [];
  for (const r of centres) {
    for (const c of centres) {
      const nearFinder = (r <= 8 && c <= 8)
        || (r <= 8 && c >= size - 9)
        || (r >= size - 9 && c <= 8);
      if (nearFinder) continue;
      for (let dr = -2; dr <= 2; dr += 1) {
        for (let dc = -2; dc <= 2; dc += 1) {
          grid[r + dr]![c + dc] = Math.max(Math.abs(dr), Math.abs(dc)) !== 1;
        }
      }
    }
  }

  // The one module that is always dark.
  grid[size - 8]![8] = true;

  // Reserve the format areas so data does not land in them.
  for (let i = 0; i <= 8; i += 1) {
    if (grid[8]![i] === null) grid[8]![i] = false;
    if (grid[i]![8] === null) grid[i]![8] = false;
  }
  for (let i = 0; i < 8; i += 1) {
    if (grid[8]![size - 1 - i] === null) grid[8]![size - 1 - i] = false;
    if (grid[size - 1 - i]![8] === null) grid[size - 1 - i]![8] = false;
  }

  // And the version areas, from version 7 up.
  if (version >= 7) {
    const info = VERSION_INFO[version]!;
    for (let i = 0; i < 18; i += 1) {
      const bit = ((info >> i) & 1) === 1;
      const r = Math.floor(i / 3);
      const c = i % 3;
      grid[size - 11 + c]![r] = bit;
      grid[r]![size - 11 + c] = bit;
    }
  }
}

/** True where a module belongs to a function pattern and must not be masked. */
function reservedMap(version: number, size: number): boolean[][] {
  const probe = blankGrid(size);
  placeFunctionPatterns(probe, version);
  return probe.map((row) => row.map((cell) => cell !== null));
}

/** Walks the zigzag the spec defines: two columns at a time, right to left. */
function placeData(grid: Grid, codewords: Uint8Array): void {
  const size = grid.length;
  let bitIndex = 0;
  let upward = true;
  for (let right = size - 1; right >= 1; right -= 2) {
    // Column 6 is the vertical timing pattern; the pairing skips over it.
    if (right === 6) right = 5;
    for (let step = 0; step < size; step += 1) {
      const row = upward ? size - 1 - step : step;
      for (const col of [right, right - 1]) {
        if (grid[row]![col] !== null) continue;
        const byte = codewords[bitIndex >> 3] ?? 0;
        grid[row]![col] = ((byte >> (7 - (bitIndex & 7))) & 1) === 1;
        bitIndex += 1;
      }
    }
    upward = !upward;
  }
}

const MASKS: Array<(r: number, c: number) => boolean> = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

/** The four penalty rules. Lower is more readable; the best mask wins. */
function penalty(m: boolean[][]): number {
  const size = m.length;
  let score = 0;

  // Rule 1: runs of five or more of the same colour.
  for (let i = 0; i < size; i += 1) {
    for (const line of [m[i]!, m.map((row) => row[i]!)]) {
      let run = 1;
      for (let j = 1; j < size; j += 1) {
        if (line[j] === line[j - 1]) { run += 1; } else { if (run >= 5) score += run - 2; run = 1; }
      }
      if (run >= 5) score += run - 2;
    }
  }

  // Rule 2: every 2x2 block of one colour.
  for (let r = 0; r < size - 1; r += 1) {
    for (let c = 0; c < size - 1; c += 1) {
      const v = m[r]![c];
      if (v === m[r]![c + 1] && v === m[r + 1]![c] && v === m[r + 1]![c + 1]) score += 3;
    }
  }

  // Rule 3: the finder-like 1:1:3:1:1 sequence with four light modules beside it.
  const A = [true, false, true, true, true, false, true, false, false, false, false];
  const B = [false, false, false, false, true, false, true, true, true, false, true];
  const matches = (line: boolean[], at: number, pat: boolean[]): boolean =>
    pat.every((v, k) => line[at + k] === v);
  for (let i = 0; i < size; i += 1) {
    for (const line of [m[i]!, m.map((row) => row[i]!)]) {
      for (let j = 0; j + 11 <= size; j += 1) {
        if (matches(line, j, A) || matches(line, j, B)) score += 40;
      }
    }
  }

  // Rule 4: deviation from an even balance of dark and light.
  let dark = 0;
  for (const row of m) for (const v of row) if (v) dark += 1;
  const percent = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(percent - 50) / 5) * 10;

  return score;
}

/** 15-bit BCH format information, mask and all. */
function formatBits(level: EcLevel, mask: number): number {
  const data = (LEVEL_BITS[level] << 3) | mask;
  let rem = data << 10;
  for (let i = 14; i >= 10; i -= 1) {
    if ((rem >> i) & 1) rem ^= 0x537 << (i - 10);
  }
  return ((data << 10) | rem) ^ 0x5412;
}

function applyFormat(m: boolean[][], level: EcLevel, mask: number): void {
  const size = m.length;
  const bits = formatBits(level, mask);
  const on = (i: number): boolean => ((bits >> i) & 1) === 1;

  // These indices are [row][column], and the specification's own diagram is
  // given as (x, y) — column first. Getting that backwards transposes the whole
  // block: bits 0–5 run down column 8 and bits 9–14 run along row 8, and a
  // transposed version puts each run where the other belongs.
  //
  // It is invisible from inside. The reserved-module map covers both the row
  // and the column, so nothing overlaps; both copies still agree with each
  // other; and a reader written from the same mistaken diagram decodes it
  // perfectly. Only a real scanner, reading the correct cells, sees fifteen
  // bits that fail their own error-correcting code — and it stops there,
  // because without the format information it does not know which mask to undo.
  //
  // Every other check passed while this was wrong: valid Reed–Solomon
  // syndromes, correct finders, correct timing, faithful rendering. The symbol
  // was flawless apart from the fifteen bits that say how to read it.

  // First copy: down the left column, then back along the top row.
  for (let i = 0; i <= 5; i += 1) m[i]![8] = on(i);
  m[7]![8] = on(6);
  m[8]![8] = on(7);
  m[8]![7] = on(8);
  for (let i = 9; i <= 14; i += 1) m[8]![14 - i] = on(i);

  // Second copy: along the right of the top row, then down the bottom left.
  for (let i = 0; i <= 7; i += 1) m[8]![size - 1 - i] = on(i);
  for (let i = 8; i <= 14; i += 1) m[size - 15 + i]![8] = on(i);
}

/* ------------------------------------------------------------------ api -- */

/**
 * Encodes `text` as a QR code.
 *
 * `quietZone` is in modules and defaults to 4, which the specification
 * requires for a code to be scannable — a QR pressed against other content
 * often will not read at all.
 */
export function encodeQr(
  text: string,
  opts: { level?: EcLevel; quietZone?: number } = {},
): QrCode {
  const level = opts.level ?? 'M';
  const quiet = opts.quietZone ?? 4;
  const byteLength = utf8(text).length;
  const version = chooseVersion(byteLength, level);
  const size = version * 4 + 17;

  const grid = blankGrid(size);
  placeFunctionPatterns(grid, version);
  placeData(grid, codewordsFor(text, version, level));

  const reserved = reservedMap(version, size);
  const base = grid.map((row) => row.map((v) => v === true));

  let best: boolean[][] | null = null;
  let bestScore = Infinity;
  let bestMask = 0;
  for (let mask = 0; mask < 8; mask += 1) {
    const candidate = base.map((row, r) => row.map(
      (v, c) => (reserved[r]![c] ? v : v !== MASKS[mask]!(r, c)),
    ));
    applyFormat(candidate, level, mask);
    const score = penalty(candidate);
    if (score < bestScore) { bestScore = score; best = candidate; bestMask = mask; }
  }
  void bestMask;

  const chosen = best!;
  if (quiet === 0) return { size, modules: chosen };

  const padded = size + quiet * 2;
  const out: boolean[][] = Array.from({ length: padded }, () => new Array<boolean>(padded).fill(false));
  for (let r = 0; r < size; r += 1) {
    for (let c = 0; c < size; c += 1) out[r + quiet]![c + quiet] = chosen[r]![c]!;
  }
  return { size: padded, modules: out };
}

/**
 * The dark modules as one SVG path, which is the compact way to draw this:
 * one `<path>` rather than several hundred `<rect>` elements.
 *
 * The viewBox is in module units, so the caller sets the pixel size in CSS and
 * the code stays crisp at any of them.
 */
export function qrSvgPath(code: QrCode): string {
  const parts: string[] = [];
  for (let r = 0; r < code.size; r += 1) {
    for (let c = 0; c < code.size; c += 1) {
      if (code.modules[r]![c]) parts.push(`M${c} ${r}h1v1h-1z`);
    }
  }
  return parts.join('');
}
